import React, { useState, useEffect } from "react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Trash2 } from "lucide-react";
import { Opportunity } from "@/types/database-extensions";
import { supabase } from "@/lib/supabase";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface OpportunityCardProps {
  opportunity: Opportunity;
  onDelete?: (id: string) => Promise<void>;
  canDelete?: boolean;
}

const OpportunityCard: React.FC<OpportunityCardProps> = ({ opportunity, onDelete, canDelete: initialCanDelete }) => {
  const [canDelete, setCanDelete] = useState(initialCanDelete);
  
  // Check admin status separately if needed
  useEffect(() => {
    const checkAdminStatus = async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session.session?.user) return;
      
      const userId = session.session.user.id;
      
      // If already allowed to delete, don't check admin
      if (initialCanDelete) return;
      
      if (userId) {
        const { data } = await supabase
          .from("user_roles")
          .select("*")
          .eq("user_id", userId)
          .eq("role", "admin");
          
        setCanDelete(!!data && data.length > 0);
      }
    };
    
    checkAdminStatus();
  }, [initialCanDelete]);

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardHeader>
        <div className="flex justify-between items-start">
          <CardTitle className="text-xl">{opportunity.title}</CardTitle>
          {opportunity.type && <Badge>{opportunity.type}</Badge>}
        </div>
        <p className="text-base font-medium">{opportunity.organization}</p>
      </CardHeader>
      <CardContent>
        <p className="text-muted-foreground line-clamp-3 mb-4">
          {opportunity.description}
        </p>
        <div className="flex flex-col gap-2 text-sm">
          {opportunity.location && (
            <div className="flex items-center">
              <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>{opportunity.location}</span>
            </div>
          )}
          {opportunity.deadline && (
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
              <span>Deadline: {new Date(opportunity.deadline).toLocaleDateString()}</span>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button asChild variant="default" className="flex-1 mr-2">
          <a 
            href={opportunity.application_url || "#"} 
            target="_blank" 
            rel="noopener noreferrer"
          >
            Apply Now
          </a>
        </Button>
        
        {canDelete && onDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Opportunity</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this opportunity? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => onDelete(opportunity.id)}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </CardFooter>
    </Card>
  );
};

export default OpportunityCard;
